import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { systemPrompts, LANGUAGE_LABELS } from '@/api/systemPrompts';
import type { SystemPromptRow } from '@/api/systemPrompts';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { History, RotateCcw, Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

interface Props {
  prompt: SystemPromptRow;
}

export function PromptVersionHistory({ prompt }: Props) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['systemPrompt', prompt.id, 'versions'],
    queryFn: () => systemPrompts.listVersions(prompt.id),
  });

  const rollback = useMutation({
    mutationFn: (versionId: string) => systemPrompts.rollback(prompt.id, versionId),
    onSuccess: () => {
      // Invalidate the detail + versions so the UI reflects the restore.
      qc.invalidateQueries({ queryKey: ['systemPrompt', prompt.id] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading history…
      </div>
    );
  }

  const rows = data?.rows ?? [];

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center gap-1.5 py-8 text-center">
        <History className="h-5 w-5 text-slate-300" />
        <p className="text-xs text-slate-500">No version history yet. Edits will show up here.</p>
      </div>
    );
  }

  return (
    <div className="rounded border border-slate-200 bg-white divide-y divide-slate-100">
      {rows.map((v) => (
        <div key={v.id} className="px-3 py-2 flex items-start gap-3 text-xs">
          <div className="w-28 shrink-0">
            <span className="text-slate-700 font-medium">
              {new Date(v.changed_at).toLocaleDateString()}
            </span>
            <br />
            <span className="text-slate-400 text-[10px]">
              {new Date(v.changed_at).toLocaleTimeString()}
            </span>
          </div>

          <div className="flex flex-col gap-0.5 w-20 shrink-0">
            <Badge variant="outline" className="text-[10px] w-fit">
              {LANGUAGE_LABELS[v.language]}
            </Badge>
            <span className={cn(
              'text-[10px] text-slate-500 font-medium',
            )}>
              {v.override_id ? 'override' : 'global'}
            </span>
          </div>

          <div className="flex-1 min-w-0">
            {v.tts_text ? (
              <p className="text-slate-800 break-words" dir={v.language === 'en' ? 'ltr' : 'rtl'}>
                {v.tts_text}
              </p>
            ) : (
              <p className="italic text-slate-400">(empty / audio-only)</p>
            )}
          </div>

          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 shrink-0"
            disabled={rollback.isPending}
            onClick={() => {
              if (confirm('Restore this version? A fresh version row will be created for the rollback.')) {
                rollback.mutate(v.id);
              }
            }}
            title="Roll back to this version"
          >
            {rollback.isPending && rollback.variables === v.id
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <RotateCcw className="h-3.5 w-3.5" />}
          </Button>
        </div>
      ))}

      {rows.length >= 200 && (
        <p className="px-3 py-2 text-[10px] text-slate-400 italic">
          Showing newest 200 entries.
        </p>
      )}
    </div>
  );
}
